import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../state/siteContent";

/*  ────────────────────────────────────────────────────────────────────────
    A DAY AT NAUSIDDHI  ·  v2
    One pinned, scroll-scrubbed cinematic that plays a full day —
    sunrise → morning → golden hour → night — entirely procedural (no image
    assets) on a single high-DPR canvas, so the whole scene re-lights from
    one scroll value. Crisp text beats sit as DOM on top.

    v2 upgrades over v1:
    · 2.5× DPR backing store  → sharp on 2K / retina displays
    · Layered realistic trees (cypress + pine tiers + deciduous canopies with
      trunks and shading) instead of flat ellipses
    · Mountains with vertical shading, atmospheric haze + warm/cool rim-light
    · Water with a mirrored reflection of the ridgeline + celestial + ripples
    · Volumetric god-rays, drifting clouds, layered sun/moon corona
    · A cinematic reveal (dark pre-dawn veil lifts on entry — no hard jump
      from the map) + graceful, correctly-centred text
    ──────────────────────────────────────────────────────────────────────── */

/* ── math helpers ───────────────────────────────────────────────────────── */
const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (a, b, t) => { const x = clamp((t - a) / (b - a)); return x * x * (3 - 2 * x); };
const env = (p, a, b, c, d) => {
  if (p <= a || p >= d) return 0;
  if (p < b) { const x = (p - a) / (b - a); return x * x * (3 - 2 * x); }
  if (p > c) { const x = 1 - (p - c) / (d - c); return x * x * (3 - 2 * x); }
  return 1;
};
const lerpRGB = (c1, c2, t) => [
  Math.round(lerp(c1[0], c2[0], t)),
  Math.round(lerp(c1[1], c2[1], t)),
  Math.round(lerp(c1[2], c2[2], t)),
];
const rgb = (c, a = 1) => (a >= 1 ? `rgb(${c[0]},${c[1]},${c[2]})` : `rgba(${c[0]},${c[1]},${c[2]},${a})`);
const shade = (c, f) => [clamp(c[0] * f, 0, 255), clamp(c[1] * f, 0, 255), clamp(c[2] * f, 0, 255)];

/* ── time-of-day palette anchors ────────────────────────────────────────── */
const STOPS = [0.0, 0.3, 0.62, 0.82, 1.0];
const PAL = [
  { /* SUNRISE */
    skyTop:[36,44,92], skyMid:[120,104,150], skyHor:[250,190,128],
    sun:[255,222,158], sunGlow:[255,196,128],
    hillFar:[128,128,158], hillMid:[92,110,120], hillNear:[46,66,62], trees:[26,46,40], treeLo:[16,32,28],
    building:[54,64,74], win:[255,214,150], water:[150,150,176], mist:[236,224,236],
    moon:[236,240,255], cloud:[250,214,190],
    aStar:0.12, aBird:0.0, aFly:0.0, aMist:0.72, aSun:0.9, aMoon:0.0, aWin:0.0, aRay:0.5, aCloud:0.55, rim:[255,206,150], aRim:0.5 },
  { /* MORNING */
    skyTop:[92,150,208], skyMid:[152,198,228], skyHor:[220,238,242],
    sun:[255,252,240], sunGlow:[255,248,220],
    hillFar:[156,182,196], hillMid:[112,154,144], hillNear:[60,100,78], trees:[34,68,48], treeLo:[22,48,34],
    building:[124,140,152], win:[255,248,206], water:[150,192,208], mist:[228,240,244],
    moon:[236,240,255], cloud:[255,255,255],
    aStar:0.0, aBird:1.0, aFly:0.0, aMist:0.14, aSun:0.98, aMoon:0.0, aWin:0.0, aRay:0.7, aCloud:0.85, rim:[255,255,240], aRim:0.35 },
  { /* GOLDEN HOUR */
    skyTop:[94,98,152], skyMid:[220,150,120], skyHor:[255,176,90],
    sun:[255,196,108], sunGlow:[255,146,68],
    hillFar:[176,142,150], hillMid:[144,104,92], hillNear:[90,68,54], trees:[54,46,36], treeLo:[38,32,26],
    building:[124,98,88], win:[255,206,130], water:[224,160,108], mist:[244,214,192],
    moon:[236,240,255], cloud:[255,196,150],
    aStar:0.05, aBird:0.35, aFly:0.0, aMist:0.2, aSun:0.92, aMoon:0.0, aWin:0.2, aRay:0.9, aCloud:0.7, rim:[255,180,110], aRim:0.7 },
  { /* DUSK */
    skyTop:[28,30,68], skyMid:[86,64,110], skyHor:[202,110,90],
    sun:[255,148,94], sunGlow:[255,116,78],
    hillFar:[72,68,98], hillMid:[52,54,72], hillNear:[30,38,44], trees:[18,26,28], treeLo:[12,18,20],
    building:[40,42,58], win:[255,196,120], water:[70,66,100], mist:[122,110,140],
    moon:[238,242,255], cloud:[150,110,120],
    aStar:0.55, aBird:0.04, aFly:0.42, aMist:0.2, aSun:0.22, aMoon:0.42, aWin:0.72, aRay:0.35, aCloud:0.5, rim:[180,150,190], aRim:0.5 },
  { /* NIGHT */
    skyTop:[7,11,30], skyMid:[13,20,50], skyHor:[24,38,64],
    sun:[255,148,94], sunGlow:[255,116,78],
    hillFar:[22,30,54], hillMid:[16,24,42], hillNear:[10,18,26], trees:[6,12,16], treeLo:[4,8,11],
    building:[16,22,36], win:[255,206,140], water:[16,26,52], mist:[40,50,80],
    moon:[240,244,255], cloud:[40,52,86],
    aStar:1.0, aBird:0.0, aFly:1.0, aMist:0.12, aSun:0.0, aMoon:0.96, aWin:1.0, aRay:0.0, aCloud:0.4, rim:[150,180,255], aRim:0.55 },
];
function palAt(p) {
  let i = 0;
  while (i < STOPS.length - 2 && p > STOPS[i + 1]) i++;
  const t = clamp((p - STOPS[i]) / (STOPS[i + 1] - STOPS[i]));
  const A = PAL[i], B = PAL[i + 1], out = {};
  for (const k in A) out[k] = Array.isArray(A[k]) ? lerpRGB(A[k], B[k], t) : lerp(A[k], B[k], t);
  return out;
}
function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function DayAtNausiddhi() {
  const { home } = useContent();
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const canvasRef = useRef(null);

  const cueRef = useRef(null);
  const heroRef = useRef(null);
  const capRefs = [useRef(null), useRef(null), useRef(null)];
  const amenRef = useRef(null);           // single, consistent amenity slot
  const amenTextRef = useRef(null);
  const finalRef = useRef(null);
  const ctaRef = useRef(null);
  const phaseLabelRef = useRef(null);
  const railFillRef = useRef(null);
  const scrimRef = useRef(null);

  const pRef = useRef(0);
  const runningRef = useRef(false);
  const geomRef = useRef(null);
  const dprRef = useRef(1);
  const reducedRef = useRef(false);

  const AMENITIES = ["The Clubhouse", "Open lawns & seating", "Meditation garden", "Reflecting waters"];

  useEffect(() => {
    const section = sectionRef.current;
    const canvas = canvasRef.current;
    if (!section || !canvas) return;
    const ctx = canvas.getContext("2d");
    reducedRef.current = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* ── geometry (stable across resizes) ───────────────────────────────── */
    function buildGeometry(W, H) {
      const rnd = mulberry32(4801);
      const ridge = (baseY, amp, rough, steps, seed) => {
        const pts = []; const ph = seed;
        for (let i = 0; i <= steps; i++) {
          const x = (i / steps) * W;
          const n = Math.sin(i * rough + ph) * 0.6 + Math.sin(i * rough * 2.3 + ph * 1.7) * 0.28
                  + Math.sin(i * rough * 0.5 + ph * 0.5) * 0.5 + Math.sin(i * rough * 4.1 + ph) * 0.12;
          pts.push([x, baseY * H + n * amp]);
        }
        return pts;
      };
      const yAt = (pts, x) => {
        const seg = W / (pts.length - 1);
        let i = Math.max(0, Math.min(pts.length - 2, Math.floor(x / seg)));
        const t = (x - pts[i][0]) / (pts[i + 1][0] - pts[i][0] || 1);
        return lerp(pts[i][1], pts[i + 1][1], clamp(t));
      };

      const hillFar = ridge(0.50, H * 0.06, 0.42, 44, rnd() * 10);
      const hillMid = ridge(0.585, H * 0.05, 0.7, 52, rnd() * 10);
      const hillNear = ridge(0.66, H * 0.045, 1.0, 60, rnd() * 10);

      // trees: type 0 cypress (tall pointed), 1 pine (tiered), 2 deciduous (round canopy)
      const makeTrees = (ridgePts, count, scale, band) => {
        const arr = [];
        for (let i = 0; i <= count; i++) {
          const x = (i / count) * W + (rnd() - 0.5) * (W / count) * 0.7;
          const type = rnd() < 0.5 ? 0 : rnd() < 0.6 ? 1 : 2;
          arr.push({
            x, baseY: yAt(ridgePts, clamp(x, 0, W)) + 2,
            h: H * scale * (0.7 + rnd() * 0.6),
            w: (10 + rnd() * 10) * (band === "near" ? 1.25 : 0.8),
            type, sway: rnd() * Math.PI * 2, tone: 0.85 + rnd() * 0.3,
          });
        }
        return arr;
      };
      const treesFar = makeTrees(hillMid, Math.floor(W / 26), 0.04, "far");
      const treesNear = makeTrees(hillNear, Math.floor(W / 46), 0.075, "near");

      // clouds — clusters of ellipse puffs
      const clouds = [];
      for (let i = 0; i < 6; i++) {
        const cx = rnd() * W, cy = H * (0.08 + rnd() * 0.26), s = 0.6 + rnd() * 0.9;
        const puffs = [];
        const n = 4 + Math.floor(rnd() * 4);
        for (let j = 0; j < n; j++)
          puffs.push({ dx: (j - n / 2) * 34 * s + (rnd() - 0.5) * 20, dy: (rnd() - 0.5) * 16 * s, r: (26 + rnd() * 26) * s });
        clouds.push({ x: cx, y: cy, puffs, speed: 3 + rnd() * 5, w: n * 34 * s });
      }

      const stars = [];
      for (let i = 0; i < 150; i++)
        stars.push({ x: rnd() * W, y: rnd() * H * 0.6, r: rnd() * 1.3 + 0.3, ph: rnd() * 6.28, sp: 0.6 + rnd() * 1.6 });

      const flies = [];
      for (let i = 0; i < 50; i++)
        flies.push({ x: rnd() * W, y: H * (0.5 + rnd() * 0.2), r: rnd() * 1.5 + 0.8, ph: rnd() * 6.28, sp: 0.4 + rnd() * 0.9, drift: 8 + rnd() * 26 });

      const flocks = [];
      for (let f = 0; f < 3; f++) {
        const n = 5 + Math.floor(rnd() * 5), members = [];
        for (let i = 0; i < n; i++)
          members.push({ ox: (i - n / 2) * (10 + rnd() * 8), oy: Math.abs(i - n / 2) * (5 + rnd() * 4) - rnd() * 6, ph: rnd() * 6.28 });
        flocks.push({ y: H * (0.15 + rnd() * 0.22), size: 5 + rnd() * 4, speed: 0.6 + rnd() * 0.5, offset: rnd(), members });
      }

      // Landscape amenity glows - low, grounded details that suggest a
      // developed plotted community without drawing apartment towers.
      const amenityLights = [];
      const cx = W * (0.40 + rnd() * 0.12), groundY = H * 0.648;
      for (let i = 0; i < 18; i++) {
        amenityLights.push({
          x: cx - W * 0.16 + i * W * 0.018 + (rnd() - 0.5) * W * 0.006,
          y: groundY - H * (0.012 + rnd() * 0.012),
          r: 1.5 + rnd() * 1.8,
          tw: rnd() * 6.28,
        });
      }

      return { W, H, hillFar, hillMid, hillNear, treesFar, treesNear, clouds, stars, flies, flocks, amenityLights, groundY, waterTop: H * 0.72, yAt };
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      dprRef.current = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      geomRef.current = buildGeometry(rect.width, rect.height);
    }
    resize();
    window.addEventListener("resize", resize);

    /* ── celestial positions ────────────────────────────────────────────── */
    const sunPos = (p, W, H) => { const s = clamp(p / 0.72); return { x: lerp(0.12, 0.88, s) * W, y: (0.66 - 0.46 * Math.sin(Math.PI * s)) * H }; };
    const moonPos = (p, W, H) => { const m = clamp((p - 0.74) / 0.26); return { x: lerp(0.74, 0.44, m) * W, y: lerp(0.42, 0.15, m) * H }; };

    function fillRidge(pts, dx, dyTop, topCol, botCol, H) {
      const top = Math.min(...pts.map((q) => q[1]));
      const grd = ctx.createLinearGradient(0, top + dyTop, 0, H);
      grd.addColorStop(0, rgb(topCol)); grd.addColorStop(1, rgb(botCol));
      ctx.beginPath(); ctx.moveTo(pts[0][0] + dx, pts[0][1] + dyTop);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] + dx, pts[i][1] + dyTop);
      ctx.lineTo(pts[pts.length - 1][0] + dx, H); ctx.lineTo(pts[0][0] + dx, H); ctx.closePath();
      ctx.fillStyle = grd; ctx.fill();
    }
    function rimLight(pts, dx, dyTop, col, alpha) {
      if (alpha < 0.02) return;
      ctx.beginPath(); ctx.moveTo(pts[0][0] + dx, pts[0][1] + dyTop);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] + dx, pts[i][1] + dyTop);
      ctx.strokeStyle = rgb(col, alpha); ctx.lineWidth = 1.4; ctx.stroke();
    }

    // draw one tree (shape by type) at (x, baseY) with height h, width w
    function drawTree(x, baseY, h, w, type, col, loCol, hiCol, sway) {
      const topY = baseY - h;
      if (type === 0) {
        // cypress: tall tapered spindle with slight lean (sway) + trunk
        ctx.fillStyle = rgb([ (col[0]+loCol[0])/2|0,(col[1]+loCol[1])/2|0,(col[2]+loCol[2])/2|0 ]);
        ctx.fillRect(x - w * 0.06, baseY - h * 0.12, w * 0.12, h * 0.14);
        const lean = sway * 0.6;
        const g = ctx.createLinearGradient(x, topY, x, baseY);
        g.addColorStop(0, rgb(hiCol)); g.addColorStop(0.5, rgb(col)); g.addColorStop(1, rgb(loCol));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(x + lean, topY);
        ctx.quadraticCurveTo(x + w * 0.5, baseY - h * 0.5, x + w * 0.34, baseY - h * 0.02);
        ctx.quadraticCurveTo(x, baseY + 1, x - w * 0.34, baseY - h * 0.02);
        ctx.quadraticCurveTo(x - w * 0.5, baseY - h * 0.5, x + lean, topY);
        ctx.closePath(); ctx.fill();
      } else if (type === 1) {
        // pine: 3 stacked tiers + trunk
        ctx.fillStyle = rgb(loCol);
        ctx.fillRect(x - w * 0.07, baseY - h * 0.1, w * 0.14, h * 0.12);
        for (let t = 0; t < 3; t++) {
          const ty = baseY - h * 0.12 - t * h * 0.28;
          const tw = w * (0.62 - t * 0.16), th = h * 0.34;
          const g = ctx.createLinearGradient(x - tw, ty - th, x + tw, ty);
          g.addColorStop(0, rgb(hiCol)); g.addColorStop(1, rgb(t === 0 ? loCol : col));
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.moveTo(x + sway * 0.4, ty - th); ctx.lineTo(x + tw, ty); ctx.lineTo(x - tw, ty); ctx.closePath(); ctx.fill();
        }
      } else {
        // deciduous: trunk + overlapping canopy blobs
        ctx.fillStyle = rgb(loCol);
        ctx.fillRect(x - w * 0.08, baseY - h * 0.34, w * 0.16, h * 0.34);
        const cy = baseY - h * 0.55, cw = w * 0.62, chh = h * 0.5;
        const blobs = [[0, 0, 1], [-cw * 0.55, chh * 0.1, 0.7], [cw * 0.55, chh * 0.12, 0.72], [sway * 0.6, -chh * 0.4, 0.72]];
        for (const [bx, by, br] of blobs) {
          const g = ctx.createRadialGradient(x + bx - cw * 0.2, cy + by - chh * 0.2, 2, x + bx, cy + by, cw * br);
          g.addColorStop(0, rgb(hiCol)); g.addColorStop(1, rgb(col));
          ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(x + bx, cy + by, cw * br, chh * br, 0, 0, 6.28); ctx.fill();
        }
      }
    }

    /* ── main draw ──────────────────────────────────────────────────────── */
    let startT = performance.now();
    function draw(now) {
      const g = geomRef.current; if (!g) return;
      const dpr = dprRef.current, W = g.W, H = g.H, p = pRef.current;
      const t = reducedRef.current ? 0 : (now - startT) / 1000;
      const pal = palAt(p);
      const drift = p * W * 0.045;
      const pull = smooth(0.82, 1.0, p);
      const sPos = sunPos(p, W, H), mPos = moonPos(p, W, H);
      const lightX = pal.aSun > pal.aMoon ? sPos.x : mPos.x;
      const lightAbove = (pal.aSun > pal.aMoon ? sPos.y : mPos.y);

      ctx.save(); ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);

      // 1 ─ sky
      const sky = ctx.createLinearGradient(0, 0, 0, H * 0.78);
      sky.addColorStop(0, rgb(pal.skyTop)); sky.addColorStop(0.5, rgb(pal.skyMid)); sky.addColorStop(1, rgb(pal.skyHor));
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);

      // 2 ─ stars
      if (pal.aStar > 0.01) {
        ctx.fillStyle = "#eef3ff";
        for (const s of g.stars) { const tw = 0.55 + 0.45 * Math.sin(t * s.sp + s.ph); ctx.globalAlpha = pal.aStar * tw; ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, 6.28); ctx.fill(); }
        ctx.globalAlpha = 1;
      }

      // 3 ─ god-rays from the sun (soft radial beams)
      if (pal.aRay > 0.02 && pal.aSun > 0.1) {
        ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.translate(sPos.x, sPos.y);
        const beams = 9;
        for (let i = 0; i < beams; i++) {
          const ang = (i / beams) * Math.PI * 2 + t * 0.03;
          const len = Math.max(W, H) * 0.9;
          const gr = ctx.createLinearGradient(0, 0, Math.cos(ang) * len, Math.sin(ang) * len);
          gr.addColorStop(0, rgb(pal.sunGlow, 0.10 * pal.aRay));
          gr.addColorStop(1, rgb(pal.sunGlow, 0));
          ctx.fillStyle = gr; ctx.rotate((Math.PI * 2) / beams);
          ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(len, -len * 0.05); ctx.lineTo(len, len * 0.05); ctx.closePath(); ctx.fill();
        }
        ctx.restore();
      }

      // 4 ─ celestial body with layered corona
      const drawBody = (pos, col, glow, alpha, radius) => {
        if (alpha < 0.01) return;
        for (let k = 3; k >= 1; k--) {
          const rr = radius * (2 + k * 2.4);
          const gg = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, rr);
          gg.addColorStop(0, rgb(glow, (0.22 / k) * alpha)); gg.addColorStop(1, rgb(glow, 0));
          ctx.fillStyle = gg; ctx.fillRect(pos.x - rr, pos.y - rr, rr * 2, rr * 2);
        }
        ctx.globalAlpha = alpha; ctx.fillStyle = rgb(col);
        ctx.beginPath(); ctx.arc(pos.x, pos.y, radius, 0, 6.28); ctx.fill();
        ctx.globalAlpha = 1;
      };
      drawBody(sPos, pal.sun, pal.sunGlow, pal.aSun, Math.min(W, H) * 0.05);
      drawBody(mPos, pal.moon, [190, 210, 255], pal.aMoon, Math.min(W, H) * 0.036);
      // moon craters
      if (pal.aMoon > 0.1) {
        ctx.globalAlpha = pal.aMoon * 0.12; ctx.fillStyle = "#8b9bc4";
        const r = Math.min(W, H) * 0.036;
        [[-.3,-.2,.22],[.25,.1,.28],[.05,.35,.18]].forEach(([a,b,c])=>{ctx.beginPath();ctx.arc(mPos.x+a*r,mPos.y+b*r,c*r,0,6.28);ctx.fill();});
        ctx.globalAlpha = 1;
      }

      // 5 ─ drifting clouds
      if (pal.aCloud > 0.02) {
        for (const cl of g.clouds) {
          const cx = ((cl.x + t * cl.speed) % (W + cl.w + 200)) - cl.w - 100;
          for (const pf of cl.puffs) {
            const px = cx + pf.dx, py = cl.y + pf.dy;
            const gr = ctx.createRadialGradient(px, py - pf.r * 0.3, 2, px, py, pf.r);
            gr.addColorStop(0, rgb(pal.cloud, 0.5 * pal.aCloud)); gr.addColorStop(1, rgb(pal.cloud, 0));
            ctx.fillStyle = gr; ctx.beginPath(); ctx.ellipse(px, py, pf.r, pf.r * 0.62, 0, 0, 6.28); ctx.fill();
          }
        }
      }

      // 6 ─ far hills (hazy, atmospheric) + rim
      const far = lerpRGB(pal.hillFar, pal.skyHor, 0.35);
      fillRidge(g.hillFar, -drift * 0.28, 0, far, shade(pal.hillFar, 0.8), H);
      rimLight(g.hillFar, -drift * 0.28, 0, pal.rim, pal.aRim * 0.5);

      // 7 ─ mist behind mid hill
      if (pal.aMist > 0.02) {
        for (let i = 0; i < 3; i++) {
          const my = H * (0.5 + i * 0.045);
          const mg = ctx.createLinearGradient(0, my - 44, 0, my + 60);
          mg.addColorStop(0, rgb(pal.mist, 0)); mg.addColorStop(0.5, rgb(pal.mist, pal.aMist * (0.5 - i * 0.12))); mg.addColorStop(1, rgb(pal.mist, 0));
          ctx.fillStyle = mg; ctx.fillRect(0, my - 44, W, 104);
        }
      }

      // 8 - mid hills + far tree band + low amenity glows
      ctx.save(); ctx.translate(-drift * 0.6, 0);
      fillRidge(g.hillMid, 0, 0, shade(pal.hillMid, 1.08), shade(pal.hillMid, 0.82), H);
      rimLight(g.hillMid, 0, 0, pal.rim, pal.aRim * 0.7);
      // far trees (small, hazy)
      const ftHi = lerpRGB(pal.trees, pal.skyHor, 0.25), ftCol = lerpRGB(pal.trees, pal.hillMid, 0.3), ftLo = pal.treeLo;
      for (const tr of g.treesFar) {
        const sway = reducedRef.current ? 0 : Math.sin(t * 0.7 + tr.sway) * 1.6;
        drawTree(tr.x, tr.baseY, tr.h, tr.w, tr.type, ftCol, ftLo, ftHi, sway);
      }
      if (pal.aWin > 0.02) {
        for (const l of g.amenityLights) {
          const tw = 0.62 + 0.38 * Math.sin(t * 0.9 + l.tw);
          const glow = ctx.createRadialGradient(l.x, l.y, 0, l.x, l.y, l.r * 8);
          glow.addColorStop(0, rgb(pal.win, pal.aWin * tw * 0.45));
          glow.addColorStop(1, rgb(pal.win, 0));
          ctx.fillStyle = glow;
          ctx.fillRect(l.x - l.r * 8, l.y - l.r * 8, l.r * 16, l.r * 16);
          ctx.globalAlpha = pal.aWin * tw;
          ctx.fillStyle = rgb(pal.win);
          ctx.beginPath(); ctx.arc(l.x, l.y, l.r, 0, 6.28); ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
      ctx.restore();

      // 9 ─ birds
      if (pal.aBird > 0.02) {
        ctx.strokeStyle = rgb([40, 48, 58], pal.aBird); ctx.lineWidth = 1.4; ctx.lineCap = "round";
        for (const fl of g.flocks) {
          const prog = (t * fl.speed * 0.06 + fl.offset) % 1.3 - 0.15, bx = prog * W;
          for (const m of fl.members) {
            const flap = Math.sin(t * 6 + m.ph) * fl.size * 0.5, x = bx + m.ox, y = fl.y + m.oy + Math.sin(t * 0.5 + m.ph) * 4;
            ctx.beginPath(); ctx.moveTo(x - fl.size, y + flap); ctx.lineTo(x, y); ctx.lineTo(x + fl.size, y + flap); ctx.stroke();
          }
        }
      }

      // 10 ─ near hill + detailed tree line (with directional light)
      ctx.save(); ctx.translate(-drift, pull * H * 0.03);
      fillRidge(g.hillNear, 0, 0, shade(pal.hillNear, 1.12), shade(pal.hillNear, 0.7), H);
      rimLight(g.hillNear, 0, 0, pal.rim, pal.aRim);
      const ntHi = lerpRGB(pal.trees, pal.rim, 0.28 * pal.aRim + 0.1), ntCol = pal.trees, ntLo = pal.treeLo;
      for (const tr of g.treesNear) {
        const sway = reducedRef.current ? 0 : Math.sin(t * 0.8 + tr.sway) * 3;
        // light side depends on where the sun/moon is
        const hi = tr.x < lightX ? shade(ntHi, 1.05) : shade(ntHi, 0.9);
        drawTree(tr.x, tr.baseY, tr.h, tr.w, tr.type, shade(ntCol, tr.tone), ntLo, hi, sway);
      }
      ctx.restore();

      // 11 ─ lake with mirrored reflection + ripples
      const wTop = g.waterTop;
      const wg = ctx.createLinearGradient(0, wTop, 0, H);
      wg.addColorStop(0, rgb(lerpRGB(pal.water, pal.skyHor, 0.3), 0.96));
      wg.addColorStop(1, rgb(shade(pal.water, 0.7)));
      ctx.fillStyle = wg; ctx.fillRect(0, wTop, W, H - wTop);
      // mirrored near-ridge silhouette
      ctx.save();
      ctx.beginPath(); ctx.rect(0, wTop, W, H - wTop); ctx.clip();
      ctx.globalAlpha = 0.28;
      ctx.beginPath();
      const np = g.hillNear;
      ctx.moveTo(np[0][0] - drift, 2 * wTop - (np[0][1] + pull * H * 0.03));
      for (let i = 1; i < np.length; i++) ctx.lineTo(np[i][0] - drift, 2 * wTop - (np[i][1] + pull * H * 0.03));
      ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
      ctx.fillStyle = rgb(shade(pal.hillNear, 0.85)); ctx.fill();
      ctx.globalAlpha = 1; ctx.restore();
      // celestial reflection streak
      const refB = pal.aMoon > pal.aSun ? { pos: mPos, col: pal.moon, a: pal.aMoon } : { pos: sPos, col: pal.sun, a: pal.aSun };
      if (refB.a > 0.05 && refB.pos.y < wTop) {
        for (let i = 0; i < 16; i++) {
          const ry = wTop + i * ((H - wTop) / 16), wob = Math.sin(t * 2 + i * 0.9) * (6 + i);
          ctx.globalAlpha = refB.a * (0.3 - i * 0.017); ctx.fillStyle = rgb(refB.col);
          ctx.fillRect(refB.pos.x - 12 + wob, ry, 24, 3);
        }
        ctx.globalAlpha = 1;
      }
      // ripple shimmer
      ctx.globalAlpha = 0.07; ctx.strokeStyle = "#ffffff";
      for (let i = 0; i < 8; i++) { const ry = wTop + 10 + i * ((H - wTop) / 9); ctx.beginPath(); ctx.moveTo(0, ry + Math.sin(t + i) * 2); ctx.lineTo(W, ry + Math.sin(t + i + 2) * 2); ctx.stroke(); }
      ctx.globalAlpha = 1;
      // shoreline glow
      const sh = ctx.createLinearGradient(0, wTop - 6, 0, wTop + 10);
      sh.addColorStop(0, rgb(pal.rim, 0)); sh.addColorStop(0.5, rgb(pal.rim, 0.12 * pal.aRim)); sh.addColorStop(1, rgb(pal.rim, 0));
      ctx.fillStyle = sh; ctx.fillRect(0, wTop - 6, W, 16);

      // 12 ─ fireflies
      if (pal.aFly > 0.02) {
        for (const f of g.flies) {
          const gl = 0.4 + 0.6 * Math.sin(t * f.sp + f.ph); if (gl <= 0) continue;
          const fx = f.x + Math.sin(t * 0.3 + f.ph) * f.drift, fy = f.y + Math.cos(t * 0.24 + f.ph) * (f.drift * 0.4);
          const gr = ctx.createRadialGradient(fx, fy, 0, fx, fy, f.r * 4);
          gr.addColorStop(0, `rgba(255,224,150,${pal.aFly * gl})`); gr.addColorStop(1, "rgba(255,224,150,0)");
          ctx.fillStyle = gr; ctx.fillRect(fx - f.r * 4, fy - f.r * 4, f.r * 8, f.r * 8);
        }
      }

      // 13 ─ atmospheric haze near horizon + vignette
      const hz = ctx.createLinearGradient(0, H * 0.5, 0, H * 0.74);
      hz.addColorStop(0, rgb(pal.skyHor, 0)); hz.addColorStop(1, rgb(pal.skyHor, 0.14 * (1 - pull)));
      ctx.fillStyle = hz; ctx.fillRect(0, H * 0.5, W, H * 0.24);
      const vg = ctx.createRadialGradient(W / 2, H * 0.48, H * 0.34, W / 2, H * 0.5, H * 0.9);
      vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, `rgba(0,0,0,${lerp(0.22, 0.42, pull)})`);
      ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);

      // 14 ─ cinematic entry: a gentle darkening lifts over the first ~3.5%
      // (smooth handoff from the map) — dim, never a full-black blank frame.
      const veil = (1 - smooth(0.0, 0.035, p)) * 0.5;
      if (veil > 0.001) {
        const vgd = ctx.createLinearGradient(0, 0, 0, H);
        vgd.addColorStop(0, `rgba(3,10,7,${(veil * 1.15).toFixed(3)})`);
        vgd.addColorStop(1, `rgba(3,10,7,${(veil * 0.7).toFixed(3)})`);
        ctx.fillStyle = vgd; ctx.fillRect(0, 0, W, H);
      }

      ctx.restore();
    }

    /* ── beat overlay updates ───────────────────────────────────────────── */
    const PHASES = [[0.0, "Sunrise"], [0.24, "Morning"], [0.52, "Golden Hour"], [0.8, "Night"]];
    const AMEN = (home.dayAmenities && home.dayAmenities.length === 4)
      ? home.dayAmenities
      : ["The Clubhouse", "Open lawns & seating", "Meditation garden", "Reflecting waters"];
    const AMEN_WIN = [[0.565, 0.60, 0.635, 0.665], [0.645, 0.675, 0.705, 0.735], [0.72, 0.745, 0.775, 0.80]];
    function setBeat(ref, opacity, ty = 0) {
      if (!ref.current) return;
      ref.current.style.opacity = opacity.toFixed(3);
      ref.current.style.transform = `translate(-50%,-50%) translateY(${ty.toFixed(1)}px)`;
    }
    function updateBeats(p) {
      if (cueRef.current) cueRef.current.style.opacity = env(p, 0.0, 0.006, 0.04, 0.095).toFixed(3);

      const hO = env(p, 0.008, 0.05, 0.15, 0.21);
      setBeat(heroRef, hO, (1 - hO) * 16);

      setBeat(capRefs[0], env(p, 0.25, 0.285, 0.325, 0.355), (1 - env(p, 0.25, 0.285, 0.325, 0.355)) * 10);
      setBeat(capRefs[1], env(p, 0.36, 0.39, 0.43, 0.46), (1 - env(p, 0.36, 0.39, 0.43, 0.46)) * 10);
      setBeat(capRefs[2], env(p, 0.46, 0.49, 0.53, 0.56), (1 - env(p, 0.46, 0.49, 0.53, 0.56)) * 10);

      // single amenity slot — swaps text, one at a time
      let amenO = 0, amenIdx = -1;
      for (let i = 0; i < AMEN_WIN.length; i++) { const o = env(p, ...AMEN_WIN[i]); if (o > amenO) { amenO = o; amenIdx = i; } }
      if (amenRef.current) {
        amenRef.current.style.opacity = amenO.toFixed(3);
        amenRef.current.style.transform = `translateX(-50%) translateY(${((1 - amenO) * 8).toFixed(1)}px)`;
        if (amenIdx >= 0 && amenTextRef.current && amenTextRef.current.dataset.i !== String(amenIdx)) {
          amenTextRef.current.dataset.i = String(amenIdx); amenTextRef.current.textContent = AMEN[amenIdx];
        }
      }

      const fO = env(p, 0.86, 0.93, 1.01, 1.01);
      setBeat(finalRef, fO, (1 - fO) * 16);
      if (ctaRef.current) ctaRef.current.style.pointerEvents = fO > 0.5 ? "auto" : "none";
      if (scrimRef.current) {
        // subtle dark scrim behind centred text when a big beat is active (legibility on bright sky)
        const s = Math.max(hO, env(p,0.25,0.285,0.53,0.56), fO) * 0.5;
        scrimRef.current.style.opacity = s.toFixed(3);
      }

      let phase = PHASES[0][1];
      for (const [at, name] of PHASES) if (p >= at) phase = name;
      if (phaseLabelRef.current && phaseLabelRef.current.dataset.p !== phase) { phaseLabelRef.current.dataset.p = phase; phaseLabelRef.current.textContent = phase; }
      if (railFillRef.current) railFillRef.current.style.transform = `scaleY(${p.toFixed(3)})`;
    }

    /* ── unified rAF loop ───────────────────────────────────────────────── */
    let raf = null;
    function frame(now) {
      const rect = section.getBoundingClientRect();
      const pinH = section.offsetHeight - window.innerHeight;
      let p = 0;
      if (rect.top <= 0 && rect.top >= -pinH) p = -rect.top / pinH;
      else if (rect.top < -pinH) p = 1;
      pRef.current = clamp(p);
      draw(now); updateBeats(pRef.current);
      raf = requestAnimationFrame(frame);
    }
    function start() { if (runningRef.current) return; runningRef.current = true; startT = performance.now() - 400; raf = requestAnimationFrame(frame); }
    function stop() { runningRef.current = false; if (raf) cancelAnimationFrame(raf); raf = null; }

    const io = new IntersectionObserver((es) => {
      for (const e of es) {
        if (e.isIntersecting) { pinRef.current && pinRef.current.classList.add("is-in"); start(); }
        else stop();
      }
    }, { rootMargin: "12% 0px 12% 0px" });
    io.observe(section);

    return () => { io.disconnect(); stop(); window.removeEventListener("resize", resize); };
  }, []);

  return (
    <section ref={sectionRef} className="day-section" aria-label="A Day at Nausiddhi">
      <div ref={pinRef} className="day-pin">
        <canvas ref={canvasRef} className="day-canvas" aria-hidden="true" />
        <div className="day-grain" aria-hidden="true" />
        <div ref={scrimRef} className="day-scrim" aria-hidden="true" />

        <div className="day-wayfind" aria-hidden="true">
          <span className="day-eyebrow">A Day at Nausiddhi</span>
          <span ref={phaseLabelRef} className="day-phase" data-p="Sunrise">Sunrise</span>
          <span className="day-rail"><span ref={railFillRef} className="day-rail-fill" /></span>
        </div>

        <div className="day-beats">
          <div ref={cueRef} className="day-cue">
            <span>{home.dayScrollCue}</span>
            <span className="day-cue-line" />
          </div>

          <h2 ref={heroRef} className="day-hero">
            {home.dayHeroLine1}<br />
            {home.dayHeroLine2} <em>{home.dayHeroEmphasis}</em>
          </h2>

          <p ref={capRefs[0]} className="day-cap">{home.dayCaptions?.[0]}</p>
          <p ref={capRefs[1]} className="day-cap">{home.dayCaptions?.[1]}</p>
          <p ref={capRefs[2]} className="day-cap">{home.dayCaptions?.[2]}</p>

          <div ref={amenRef} className="day-amenity">
            <span className="day-amenity-dot" />
            <span ref={amenTextRef} className="day-amenity-text" data-i="0">{home.dayAmenities?.[0]}</span>
          </div>

          <div ref={finalRef} className="day-final">
            <h2 className="day-final-title">{home.dayFinalLine1}<br /><em>{home.dayFinalEmphasis}</em></h2>
            <span className="day-final-eyebrow">{home.dayFinalEyebrow}</span>
            <Link ref={ctaRef} to="/contact" className="day-cta">
              {home.dayCtaText}<span className="day-cta-arrow">→</span>
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .day-section { position: relative; height: 580vh; background: #04110a; }
        .day-pin {
          position: sticky; top: 0; height: 100vh; height: 100svh;
          overflow: hidden; background: #04110a;
          opacity: 0; transform: scale(1.03);
          transition: opacity 900ms ease, transform 1400ms cubic-bezier(0.16,1,0.3,1);
        }
        .day-pin.is-in { opacity: 1; transform: scale(1); }
        .day-canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
        .day-grain {
          position: absolute; inset: 0; pointer-events: none; opacity: 0.045; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }
        .day-scrim {
          position: absolute; inset: 0; pointer-events: none; opacity: 0;
          background: radial-gradient(ellipse 70% 46% at 50% 48%, rgba(6,10,16,0.55) 0%, rgba(6,10,16,0) 70%);
          transition: opacity 200ms linear;
        }

        .day-wayfind {
          position: absolute; left: clamp(20px,5vw,56px); bottom: clamp(26px,6vh,54px);
          display: flex; flex-direction: column; gap: 9px; z-index: 3; pointer-events: none;
          padding: 14px 16px 14px 0;
        }
        .day-eyebrow { font-family: var(--font-ui); font-size: 10px; letter-spacing: 0.36em; text-transform: uppercase; color: rgba(255,255,255,0.5); }
        .day-phase { font-family: var(--font-serif); font-size: clamp(19px,2vw,27px); font-style: italic; color: rgba(255,255,255,0.94); text-shadow: 0 2px 22px rgba(0,0,0,0.55); transition: color 400ms ease; }
        .day-rail { position: relative; width: 1px; height: clamp(56px,9vh,100px); background: rgba(255,255,255,0.16); overflow: hidden; }
        .day-rail-fill { position: absolute; inset: 0; background: linear-gradient(180deg, var(--color-gold-light), var(--color-gold)); transform: scaleY(0); transform-origin: top; }

        .day-beats { position: absolute; inset: 0; z-index: 2; pointer-events: none; }
        .day-beats > * { will-change: opacity, transform; }

        .day-cue { position: absolute; left: 50%; bottom: 8vh; transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; gap: 12px; opacity: 0; }
        .day-cue span:first-child { font-family: var(--font-ui); font-size: 11px; letter-spacing: 0.32em; text-transform: uppercase; color: rgba(255,255,255,0.72); }
        .day-cue-line { width: 1px; height: 46px; background: linear-gradient(180deg, rgba(255,255,255,0.7), transparent); animation: day-cue-pulse 2s ease-in-out infinite; }
        @keyframes day-cue-pulse { 0%,100% { transform: scaleY(0.5); opacity: 0.4; transform-origin: top; } 50% { transform: scaleY(1); opacity: 1; transform-origin: top; } }

        .day-hero, .day-cap, .day-final {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
          width: min(92vw, 880px); margin: 0; text-align: center; opacity: 0;
        }
        .day-hero {
          font-family: var(--font-serif); font-weight: 400;
          font-size: clamp(27px, 4.4vw, 58px); line-height: 1.18; letter-spacing: -0.005em;
          color: #fbf7ee; text-shadow: 0 4px 44px rgba(0,0,0,0.5);
        }
        .day-hero em { font-style: italic; color: var(--color-gold-light); }
        .day-cap {
          font-family: var(--font-serif); font-weight: 400;
          font-size: clamp(26px, 4vw, 54px); line-height: 1.15; letter-spacing: 0.005em;
          color: #fdfbf5; text-shadow: 0 4px 44px rgba(0,0,0,0.55);
        }
        .day-amenity {
          position: absolute; left: 50%; bottom: clamp(16vh, 20vh, 24vh); transform: translateX(-50%);
          display: inline-flex; align-items: center; gap: 12px; opacity: 0;
          padding: 13px 26px; border: 1px solid rgba(255,255,255,0.22); border-radius: 999px;
          background: rgba(16,18,22,0.28); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          box-shadow: 0 8px 40px rgba(0,0,0,0.25);
        }
        .day-amenity-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--color-gold-light); box-shadow: 0 0 10px var(--color-gold-light); flex: none; }
        .day-amenity-text { font-family: var(--font-ui); font-size: clamp(12px,1.4vw,15px); letter-spacing: 0.26em; text-transform: uppercase; color: #fdfbf5; white-space: nowrap; text-shadow: 0 2px 20px rgba(0,0,0,0.5); }

        .day-final { display: flex; flex-direction: column; align-items: center; gap: 22px; width: min(94vw, 900px); }
        .day-final-title { margin: 0; font-family: var(--font-serif); font-weight: 400; font-size: clamp(32px,5.4vw,78px); line-height: 1.1; letter-spacing: -0.01em; color: #fbf7ee; text-shadow: 0 4px 48px rgba(0,0,0,0.55); }
        .day-final-title em { font-style: italic; color: var(--color-gold-light); }
        .day-final-eyebrow { font-family: var(--font-ui); font-size: 11px; letter-spacing: 0.4em; text-transform: uppercase; color: rgba(255,255,255,0.64); }
        .day-cta {
          pointer-events: none; display: inline-flex; align-items: center; gap: 12px;
          font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 500;
          padding: 17px 40px; color: #04110a; background: var(--color-gold); border: 1px solid var(--color-gold);
          text-decoration: none; border-radius: 2px; box-shadow: 0 10px 44px rgba(212,164,92,0.3);
          transition: background 350ms ease, color 350ms ease, transform 350ms ease, box-shadow 350ms ease;
        }
        .day-cta:hover { background: transparent; color: var(--color-gold-light); transform: translateY(-2px); box-shadow: 0 14px 54px rgba(212,164,92,0.38); }
        .day-cta-arrow { transition: transform 350ms ease; }
        .day-cta:hover .day-cta-arrow { transform: translateX(5px); }

        @media (max-width: 640px) {
          .day-cap, .day-hero { width: 90vw; }
          .day-hero { font-size: clamp(24px, 6.6vw, 40px); }
          .day-cap { font-size: clamp(23px, 6.4vw, 40px); }
          /* Amenity pill was sitting at ~16-20vh from the bottom, directly
             on top of the bottom-left wayfinding (phase label + rail), which
             has a higher z-index and covered the amenity text. Raise it into
             clear space, let long labels wrap instead of overflowing the
             pill, and cap its width so it never runs under the screen edge. */
          .day-amenity {
            bottom: auto; top: 64%;
            padding: 10px 20px; max-width: 84vw; z-index: 4;
          }
          .day-amenity-text {
            letter-spacing: 0.14em; font-size: 11.5px;
            white-space: normal; text-align: center; line-height: 1.5;
          }
          /* Keep the wayfinding compact so it stays in its corner. */
          .day-wayfind { gap: 6px; left: 18px; bottom: 18px; padding: 8px 12px 8px 0; }
          .day-rail { height: 40px; }
          .day-phase { font-size: clamp(16px, 4.4vw, 20px); }
          .day-cue { bottom: 11vh; }
          .day-final { gap: 16px; width: 92vw; }
          .day-final-title { font-size: clamp(28px, 8vw, 48px); }
          .day-cta { padding: 14px 30px; font-size: 11px; letter-spacing: 0.2em; }
        }
        @media (prefers-reduced-motion: reduce) {
          .day-cue-line { animation: none; }
          .day-pin { transition: opacity 400ms ease; transform: none; }
        }

        /* Short viewports (landscape phones, small browser windows) - the
           existing @media(max-width:640px) fix only catches narrow PORTRAIT
           phones. On a short/landscape viewport the bottom-left wayfind
           block (eyebrow + phase label + vertical rail, ~145px tall) can
           occupy a third or more of the total height while the centered
           hero/caption/final text - vertically anchored to 50% - reaches
           down far enough to genuinely collide with it. Confirmed via
           scroll-through testing at 812x375: hero, all three captions, and
           the final CTA text all overlapped the wayfind block. Shrinking
           the wayfind footprint and giving it more clearance fixes this
           regardless of orientation. */
        @media (max-height: 500px) {
          .day-wayfind { gap: 4px; bottom: 10px; left: 14px; padding: 6px 10px 6px 0; }
          .day-eyebrow { display: none; }
          .day-rail { height: 22px; }
          .day-phase { font-size: 13px; }
          .day-hero, .day-cap, .day-final { top: 42%; }
          .day-hero { font-size: clamp(18px, 3.6vh, 32px); }
          .day-cap { font-size: clamp(17px, 3.4vh, 30px); }
          .day-final-title { font-size: clamp(22px, 5vh, 40px); }
          .day-final { gap: 10px; }
          .day-cta { padding: 10px 22px; font-size: 10px; }
          .day-amenity { bottom: auto; top: 78%; padding: 7px 16px; }
          .day-amenity-text { font-size: 10px; letter-spacing: 0.12em; }
          .day-cue { bottom: 4vh; }
          .day-cue-line { height: 24px; }
        }
      `}</style>
    </section>
  );
}


